import { useState } from 'react';
import { Plus, Edit2, Trash2, Palette } from 'lucide-react';
import { ThemeFormDialog } from './ThemeFormDialog';

interface Theme {
  id: string;
  label: string;
  description: string;
  preview: {
    primary: string;
    secondary: string;
  };
  tokens: {
    colors: Record<string, string>;
    typography: Record<string, string>;
    spacing: Record<string, string>;
    borders: Record<string, string>;
    shadows: Record<string, string>;
    layout: Record<string, string>;
  };
}

interface ThemesTabProps {
  themes: Theme[];
  onSave: (updatedThemes: Theme[]) => Promise<void>;
  readOnly?: boolean;
}

export function ThemesTab({ themes, onSave, readOnly = false }: ThemesTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingThemeIndex, setEditingThemeIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleAdd = () => {
    setEditingThemeIndex(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingThemeIndex(index);
    setIsDialogOpen(true);
  };

  const handleDelete = async (index: number) => {
    const theme = themes[index];
    if (!confirm(`Eliminare il tema "${theme.label}"?`)) {
      return;
    }

    setIsSaving(true);
    try {
      const updatedThemes = themes.filter((_, i) => i !== index);
      await onSave(updatedThemes);
    } catch (error) {
      console.error('Error deleting theme:', error);
      alert('Errore durante l\'eliminazione del tema');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTheme = async (theme: Theme) => {
    setIsSaving(true);
    try {
      let updatedThemes: Theme[];

      if (editingThemeIndex !== null) {
        // Update existing theme
        updatedThemes = themes.map((t, i) => (i === editingThemeIndex ? theme : t));
      } else {
        // Add new theme
        updatedThemes = [...themes, theme];
      }

      await onSave(updatedThemes);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving theme:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="themes-tab">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Themes</h2>
            <p className="text-sm text-gray-600 mt-1">
              Gestisci i temi predefiniti ({themes.length} themes)
            </p>
          </div>

          {!readOnly && (
            <button
              onClick={handleAdd}
              className="btn-primary flex items-center space-x-2"
              disabled={isSaving}
            >
              <Plus className="w-4 h-4" />
              <span>Nuovo Tema</span>
            </button>
          )}
        </div>
      </div>

      {/* Themes List */}
      <div className="px-6 py-6">
        {themes.length === 0 ? (
          <div className="text-center py-12">
            <Palette className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nessun tema disponibile</p>
            {!readOnly && (
              <button
                onClick={handleAdd}
                className="btn-secondary mt-4 inline-flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Crea il primo tema</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {themes.map((theme, index) => (
              <div
                key={theme.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
              >
                {/* Card Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {theme.label}
                      </h3>
                      <p className="text-sm text-gray-500 font-mono">{theme.id}</p>
                    </div>
                    {!readOnly && (
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={() => handleEdit(index)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                          title="Modifica"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(index)}
                          disabled={isSaving}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{theme.description}</p>
                </div>

                {/* Preview Swatches */}
                <div className="p-4 bg-gray-50">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Preview Colors
                  </p>
                  <div className="flex space-x-2">
                    <div className="flex-1">
                      <div
                        className="h-16 rounded-lg border border-gray-200"
                        style={{ backgroundColor: theme.preview.primary }}
                        title={`Primary: ${theme.preview.primary}`}
                      />
                      <p className="text-xs text-gray-600 mt-1 font-mono text-center">
                        {theme.preview.primary}
                      </p>
                    </div>
                    <div className="flex-1">
                      <div
                        className="h-16 rounded-lg border border-gray-200"
                        style={{ backgroundColor: theme.preview.secondary }}
                        title={`Secondary: ${theme.preview.secondary}`}
                      />
                      <p className="text-xs text-gray-600 mt-1 font-mono text-center">
                        {theme.preview.secondary}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Token Categories */}
                <div className="p-4 border-t border-gray-100">
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <span className="block px-2 py-1 bg-red-100 text-red-700 rounded">
                        {Object.keys(theme.tokens.colors || {}).length} colors
                      </span>
                    </div>
                    <div className="text-center">
                      <span className="block px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        {Object.keys(theme.tokens.typography || {}).length} typo
                      </span>
                    </div>
                    <div className="text-center">
                      <span className="block px-2 py-1 bg-purple-100 text-purple-700 rounded">
                        {Object.keys(theme.tokens.spacing || {}).length} space
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog */}
      {isDialogOpen && (
        <ThemeFormDialog
          theme={editingThemeIndex !== null ? themes[editingThemeIndex] : undefined}
          existingThemeIds={themes.map((t) => t.id)}
          onSave={handleSaveTheme}
          onCancel={() => setIsDialogOpen(false)}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
